import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, KeyRound, Globe, Image as ImageIcon, SlidersHorizontal } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    gemini_api_key_set: false,
    gemini_api_key_masked: "",
    gemini_model: "gemini-2.5-flash-lite",
    embedding_model: "text-embedding-004",
    similarity_threshold: 0.55,
    global_logo_url: "",
  });
  const [newKey, setNewKey] = useState("");
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([api.get("/settings"), api.get("/settings/domains")]);
      setSettings(s.data);
      setDomains(d.data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function saveSettings() {
    setSaving(true);
    try {
      const payload = {
        gemini_model: settings.gemini_model,
        embedding_model: settings.embedding_model,
        similarity_threshold: parseFloat(settings.similarity_threshold),
        global_logo_url: settings.global_logo_url,
      };
      if (newKey) payload.gemini_api_key = newKey;
      const { data } = await api.put("/settings", payload);
      setSettings(data);
      setNewKey("");
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  }

  async function addDomain() {
    if (!newDomain.trim()) return;
    try {
      await api.post("/settings/domains", { domain: newDomain.trim() });
      setNewDomain("");
      await load();
      toast.success("Domínio adicionado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }

  async function removeDomain(id) {
    try {
      await api.delete(`/settings/domains/${id}`);
      await load();
    } catch (e) { toast.error("Erro ao remover"); }
  }

  function handleLogoFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSettings({ ...settings, global_logo_url: reader.result });
    reader.readAsDataURL(file);
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Configurações globais" subtitle="Domínios autorizados, chave Gemini, modelo e logotipo global." />

      {/* Allowed domains */}
      <Card className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-[#1e5a8e]" />
          <h3 className="font-[Outfit] text-lg font-semibold text-slate-900">Domínios de e-mail permitidos</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Apenas usuários com e-mail nesses domínios conseguem entrar via Google.
        </p>
        <div className="flex gap-2 mb-4">
          <Input
            data-testid="new-domain-input"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="ex: ifpr.edu.br"
          />
          <Button data-testid="add-domain-btn" onClick={addDomain} className="bg-[#1e5a8e] hover:bg-[#164570]">
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {domains.length === 0 && <p className="text-sm text-slate-400">Nenhum domínio cadastrado.</p>}
          {domains.map((d) => (
            <Badge key={d.id} data-testid={`domain-${d.domain}`} variant="outline" className="pl-3 pr-1 py-1 gap-2 text-sm">
              @{d.domain}
              <button onClick={() => removeDomain(d.id)} className="w-5 h-5 rounded hover:bg-red-100 text-red-600 flex items-center justify-center">
                <Trash2 className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      </Card>

      {/* Gemini */}
      <Card className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-[#1e5a8e]" />
          <h3 className="font-[Outfit] text-lg font-semibold text-slate-900">Chave e modelo Gemini</h3>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Chave da API Gemini</Label>
            {settings.gemini_api_key_set ? (
              <p className="text-xs text-emerald-700 mb-1">
                Chave atual: <span className="font-mono">{settings.gemini_api_key_masked}</span> · deixe vazio para manter.
              </p>
            ) : (
              <p className="text-xs text-amber-700 mb-1">
                Nenhuma chave configurada. Sem chave, o chatbot e uploads não funcionam.
              </p>
            )}
            <Input
              data-testid="gemini-key-input"
              type="password"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="Cole a nova chave"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Modelo de chat</Label>
              <Input
                data-testid="gemini-model-input"
                value={settings.gemini_model}
                onChange={(e) => setSettings({ ...settings, gemini_model: e.target.value })}
                placeholder="gemini-2.5-flash-lite"
              />
            </div>
            <div>
              <Label>Modelo de embedding</Label>
              <Input
                data-testid="embedding-model-input"
                value={settings.embedding_model}
                onChange={(e) => setSettings({ ...settings, embedding_model: e.target.value })}
                placeholder="text-embedding-004"
              />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1"><SlidersHorizontal className="w-3.5 h-3.5" /> Limiar de similaridade ({settings.similarity_threshold})</Label>
            <Input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={settings.similarity_threshold}
              onChange={(e) => setSettings({ ...settings, similarity_threshold: e.target.value })}
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-1">Abaixo desse valor, o chatbot exibe o e-mail/telefone do setor em vez de responder.</p>
          </div>
        </div>
      </Card>

      {/* Global logo */}
      <Card className="p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="w-5 h-5 text-[#1e5a8e]" />
          <h3 className="font-[Outfit] text-lg font-semibold text-slate-900">Logotipo global (tela de login)</h3>
        </div>
        <div className="flex items-center gap-4">
          {settings.global_logo_url && (
            <img src={settings.global_logo_url} alt="logo" className="h-16 object-contain" />
          )}
          <Input data-testid="global-logo-input" type="file" accept="image/*" onChange={handleLogoFile} />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="save-settings-btn" onClick={saveSettings} disabled={saving} className="bg-[#1e5a8e] hover:bg-[#164570]">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Salvar configurações
        </Button>
      </div>
    </div>
  );
}
